import { CordataSearchWorksResponse } from "@biblioteksentralen/bmdb-search-client";
import { AgeGroup, Agent, formatIsbdTitle, Genre, Subject } from "@biblioteksentralen/cordata";
import { group, sift, sort } from "radash";
import { CollectionExpressionEntryWithWork } from "@libry-content/integrations";
import { WorkSearchResult } from "../../../../../../../searchAndCollection/searchApi/searchWorks";
import { LCWork } from "@libry-content/integrations";

export const getCollectionExpressionentryFromId = (
  work: WorkSearchResult,
  seriesWorkId: string
): CollectionExpressionEntryWithWork | undefined =>
  work.expressions
    ?.find(({ collections }) => collections.find(({ collection }) => collection.workExpressed?.id === seriesWorkId))
    ?.collections.find(({ collection }) => collection.workExpressed?.id === seriesWorkId) as
    | CollectionExpressionEntryWithWork
    | undefined;

export type CollectionMetaData = {
  collectionTitle: string;
  sortedGenres: Genre[];
  sortedAgeGroups: AgeGroup[];
  sortedSubjects: Subject[];
  sortedMainContributorAgents: (Agent & { href?: string })[];
};

export const getCollectionMetadata = (works: LCWork[], seriesWorkId: string): CollectionMetaData => {
  const genres = works.flatMap((work) => work.getGenres());
  const sortedGenres = sortByOccurrence(genres, ({ id }) => id);

  const ageGroups = works.flatMap((work) => work.ageGroups);
  const sortedAgeGroups = sortByOccurrence(ageGroups, ({ id }) => id);

  const subjects = works.flatMap((work) => work.subjects);
  const sortedSubjects = sortByOccurrence(subjects, ({ id }) => id);

  const mainContributorAgents = sift(works.map((work) => work.mainContributor)).map(({ agent }) => agent);
  const sortedMainContributorAgents = sortByOccurrence(mainContributorAgents, ({ id }) => id);
  const collectionTitle = findCollectionTitle(works, seriesWorkId);

  return { collectionTitle, sortedGenres, sortedAgeGroups, sortedSubjects, sortedMainContributorAgents };
};

const sortByOccurrence = <T extends Object>(objects: T[], getId: (object: T) => string): T[] => {
  const groupedObjects = group(objects, getId);
  const sortedObjectGroups = sift(Object.values(groupedObjects)).sort((a, b) => (b?.length ?? 0) - (a?.length ?? 0));
  const sortedUniqueObjects = sift(sortedObjectGroups.map((objectGroup) => objectGroup[0]));
  return sortedUniqueObjects;
};

export const findCollectionTitle = (works: LCWork[], seriesWorkId: string) => {
  const workTriples = works.flatMap((work) => work.getWorkTriples());
  const collectionExpressionEntry = sift(workTriples.flatMap((workTriple) => workTriple.collections)).find(
    ({ collection }) => collection.workExpressed?.id === seriesWorkId
  );
  return collectionExpressionEntry
    ? formatIsbdTitle({ title: collectionExpressionEntry.collection.title })
    : "ukjent serie";
};

export const mapCordataSearchWorksResponseToWorkSearchResult = (
  searchWorksResponses: CordataSearchWorksResponse["results"] | undefined
): WorkSearchResult[] =>
  searchWorksResponses?.map(({ work, representativeManifestationId }) => ({
    ...work,
    representativeManifestationId,
  })) ?? [];

export type WorksSortedByNumberInSeries = {
  numberedWorks: LCWork[];
  nonNumberedWorks: LCWork[];
};

export const sortSeries = (works: LCWork[], seriesWorkId: string): WorksSortedByNumberInSeries => ({
  numberedWorks: sort(
    works.filter((work) => !!getWorkSeriesNumber(work, seriesWorkId)),
    (work) => Number.parseFloat(getWorkSeriesNumber(work, seriesWorkId) ?? "0")
  ),
  nonNumberedWorks: works.filter((work) => !getWorkSeriesNumber(work, seriesWorkId)),
});

export const getWorkSeriesNumber = (work: LCWork, seriesWorkId: string): string | undefined =>
  work.getTripleBySeriesWorkId(seriesWorkId)?.collectionExpressionEntry.numberInSeries;
